/**
 * TimelineFilterDirectionSection Component
 *
 * Filter buttons for communication direction (inbound/outbound)
 */

import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { DIRECTION_OPTIONS } from "../types/timeline-filters";
import type { TimelineFilters } from "../types/timeline-filters";

interface TimelineFilterDirectionSectionProps {
  filters: TimelineFilters;
  onToggleDirection: (direction: string) => void;
}

const TimelineFilterDirectionSectionBase: React.FC<
  TimelineFilterDirectionSectionProps
> = ({ filters, onToggleDirection }) => {
  const handleToggle = useCallback(
    (direction: string) => {
      onToggleDirection(direction);
    },
    [onToggleDirection]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-sm font-semibold text-gray-700">
          Communication Direction
        </Label>
        {filters.direction?.length && (
          <Badge variant="secondary" className="text-xs">
            {filters.direction.length} selected
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {DIRECTION_OPTIONS.map((direction) => {
          const isSelected = filters.direction?.includes(direction.value);
          return (
            <Button
              key={direction.value}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => handleToggle(direction.value)}
              className={`h-10 text-sm font-medium transition-all ${
                isSelected
                  ? "bg-purple-600 text-white shadow-md transform scale-105"
                  : "hover:bg-card-50 hover:border-gray-300"
              }`}
            >
              {direction.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export const TimelineFilterDirectionSection = React.memo(
  TimelineFilterDirectionSectionBase
);
